import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid, Typography } from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "../../../../../controls/buttons/FooterButton";
import { CatalogLadle } from "../../../models/CatalogLadle";
import { useLadlesOptionsFunctions } from "../hooks/useLadlesOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { LadleModal } from "./LadleModal";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  getCatalogLadlesFromAPI: Function;
  isLoading: boolean;
  ladleId: String | undefined;
};

export const TableLadle = ({
  getCatalogLadlesFromAPI,
  isLoading,
  ladleId,
}: Props) => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [ladle, setLadle] = useState<CatalogLadle | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewLadle = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setLadle(null);
    navigate(`/configuration/steel-ladles/new`);
  };

  const { getMenuOptions } = useLadlesOptionsFunctions({
    setLadle,
    setShowModal,
    handleNewLadle,
  });
  const { rows, columnDefs } = useTableData({
    setLadle,
    setShowModal,
    showContextMenu,
  });
  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("edit");
      setLadle(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setLadle(event.data as CatalogLadle);
      setShowModal("edit");
      navigate(
        `/configuration/steel-ladles/${(event.data as CatalogLadle).LadleId}`,
      );
    }
  };

  const onCellClicked = (event: any) => {
    if (event.column.colId === "-")
      navigate(
        `/configuration/steel-ladles/${(event.data as CatalogLadle).LadleId}`,
      );
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "ladleContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({ entityId, shouldUpdate }: onModalHideProps) => {
    if (entityId && shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
        }
      }, 0);
    }
    setShowModal("");
    setLadle(null);
    navigate(`/configuration/steel-ladles/`);
  };

  useEffect(() => {
    if (ladleId) {
      if (ladleId === "new") {
        handleNewLadle();
      } else {
        let ladleNode: IRowNode<any>;

        gridApi?.forEachNode((n) => {
          if ((n.data as CatalogLadle).LadleId === Number(ladleId)) {
            ladleNode = n;
            return true;
          }
        });

        if (ladleNode!) {
          gridApi?.ensureIndexVisible(ladleNode.rowIndex ?? 0, "middle");
          ladleNode.setSelected(true);
          setLadle(ladleNode.data);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "ladleContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "1px 0 10px", fontWeight: 600 }}
          >
            Ladles
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ width: "100%", padding: "0 15px" }}
        >
          <div style={{ width: "100%", height: "70vh" }}>
            <AgGridReact<CatalogLadle>
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={columnSimpleDefaults}
              rowHeight={34}
              headerHeight={34}
              animateRows
              rowSelection="single"
              getContextMenuItems={getContextMenuItems}
              onRowDoubleClicked={onRowDoubleClicked}
              onGridReady={(params) => setGridApi(params.api)}
              getRowId={(params) => String(params.data.LadleId)}
              loading={isLoading}
              onCellClicked={onCellClicked}
            />
          </div>
          <FooterButton label="LADLE" onClick={handleNewLadle} />
        </Grid>
      </Grid>
      <LadleModal
        ladle={ladle}
        showModal={showModal}
        onHide={({ entityId, shouldUpdate }) => {
          onModalHide({ entityId, shouldUpdate });
        }}
        refreshData={getCatalogLadlesFromAPI}
      />
    </>
  );
};
